import {
  Button,
  Checkbox,
  Chips,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  Palette,
  RightPaneLayout,
  ScrollableContent,
  TabsWithContent,
  Tooltip,
  collapseColumn,
  column,
  emptyCell,
  numericColumn,
  simpleHeader,
  useModal,
} from "@homebound/beam";
import xor from "lodash/xor";
import { useCallback, useMemo, useState } from "react";
import { Loading, Price, priceCell } from "src/components";
import { useFullscreenModal } from "src/components/useFullscreenModal";
import { BidContractType, BidPackageStatus, Maybe, TakeoffLineItemVersion } from "src/generated/graphqlTypes";
import {
  BidPackageDetailActiveRequestFragment,
  PageBidPackageDetailFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";

import { DropCodeCell } from "src/routes/developments/product-offerings/components/DropCodeCell";
import { AggregateMemberVersionChangeType } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignUpdatesAvailableButton";
import { fail, isDefined, isNumber, pluralize, queryResult } from "src/utils";
import { StringParam, useQueryParams } from "use-query-params";
import { getOptionsKey } from "./BidsTab";
import { TradeCostData, sumTradeCostData } from "./BidsTab.utils";
import { ImportBidModal } from "./ImportBidModal";
import {
  PlanBasedBidsTable_AggregateVersionsQuery,
  PlanBasedBidsTable_BidPackageVersionFragment,
  PlanBasedBidsTable_PreviousTakeoffLineItemVersionFragment,
  PlanBasedBidsTable_ReadyPlanFragment,
  PlanBasedBidsTable_ReadyPlanOptionFragment,
  PlanBasedBidsTable_ScopeLinesFragment,
  PlanBasedBidsTable_TakeoffLineItemVersionFragment,
  usePlanBasedBidsTable_AggregateVersionsQuery,
  usePlanBasedBidsTable_ScopeLinesQuery,
} from "./PlanBasedBidsTable.generated";
import { AddProrationBidModal, EditProrationBidModal } from "./ProrateBidModal";
import {
  getOptionsInMultipleLocations,
  getReadyPlanOptions,
  type ReadyPlanOptionsById,
} from "./proration/PlanAndOptionTables";
import { SelectProrationModal } from "./SelectProrationModal";
import { getBidPackageTradesWithEstimate } from "./utils";

type PlanBasedBidsTableProps = {
  bidPackage: PageBidPackageDetailFragment;
  searchFilter?: string;
  requestsWithEditableBids: BidPackageDetailActiveRequestFragment[];
};

export function PlanBasedBidsTable(props: PlanBasedBidsTableProps) {
  const { bidPackage, searchFilter, requestsWithEditableBids } = props;
  const [{ versionId }] = useQueryParams({ versionId: StringParam });

  const query = usePlanBasedBidsTable_AggregateVersionsQuery({
    variables: { bidPackageVersionId: versionId ?? bidPackage.latestVersion.id },
  });

  return queryResult(query, ({ bidPackageVersion }) => (
    <PlanBasedBidsView
      bidPackage={bidPackage}
      searchFilter={searchFilter}
      requestsWithEditableBids={requestsWithEditableBids}
      bidPackageVersion={bidPackageVersion}
    />
  ));
}

type PlanBasedBidsViewProps = PlanBasedBidsTableProps & {
  bidPackageVersion: PlanBasedBidsTable_AggregateVersionsQuery["bidPackageVersion"];
};

function PlanBasedBidsView(props: PlanBasedBidsViewProps) {
  const { bidPackageVersion, ...tableProps } = props;
  const sortedRpavs = useMemo(
    () => bidPackageVersion.readyPlanAggregateVersions.sortBy((rpav) => rpav.readyPlan.id),
    [bidPackageVersion.readyPlanAggregateVersions],
  );
  const readyPlanOptionsById: ReadyPlanOptionsById = useMemo(
    () => sortedRpavs.flatMap((rpav) => rpav.readyPlan.options).keyBy((option) => option.id),
    [sortedRpavs],
  );
  const [selectedTab, setSelectedTab] = useState<string>(sortedRpavs[0].readyPlan.id);
  const selectedReadyPlan = useMemo(() => {
    return sortedRpavs.find((rpav) => rpav.readyPlan.id === selectedTab)?.readyPlan ?? fail("ReadyPlan not found");
  }, [selectedTab, sortedRpavs]);
  const [{ versionId }] = useQueryParams({ versionId: StringParam });
  const query = usePlanBasedBidsTable_ScopeLinesQuery({
    variables: {
      bidPackageVersionId: bidPackageVersion.id,
      highlightChanges: tableProps.bidPackage.status.code === BidPackageStatus.Draft && !isDefined(versionId),
      filter: { readyPlan: [selectedTab] },
    },
  });
  return (
    <TabsWithContent
      alwaysShowAllTabs
      tabs={sortedRpavs.map((rpav) => ({
        name: rpav.displayName,
        value: rpav.readyPlan.id,
        render: () => {
          // When this tab is shown, we'll be using the `readyPlan: selectedTab` to pull back only this plan's rows
          if (query.loading) return <Loading />;
          return queryResult(query, ({ bidPackageVersion, bidPackageVersionScopeLines }) => (
            <ScrollableContent virtualized>
              <RightPaneLayout>
                <div css={Css.h100.$}>
                  <PlanBasedBidsTableView
                    {...tableProps}
                    bidPackageVersion={bidPackageVersion}
                    readyPlan={selectedReadyPlan}
                    readyPlanOptionsById={readyPlanOptionsById}
                    scopeLines={bidPackageVersionScopeLines}
                  />
                </div>
              </RightPaneLayout>
            </ScrollableContent>
          ));
        },
      }))}
      selected={selectedTab}
      onChange={setSelectedTab}
    />
  );
}

type PlanBasedBidsTableViewProps = PlanBasedBidsTableProps & {
  bidPackageVersion: PlanBasedBidsTable_BidPackageVersionFragment;
  readyPlan: PlanBasedBidsTable_ReadyPlanFragment;
  readyPlanOptionsById: ReadyPlanOptionsById;
  scopeLines: PlanBasedBidsTable_ScopeLinesFragment[];
};

function PlanBasedBidsTableView(props: PlanBasedBidsTableViewProps) {
  const {
    bidPackage,
    searchFilter,
    requestsWithEditableBids,
    bidPackageVersion,
    readyPlan,
    readyPlanOptionsById,
    scopeLines,
  } = props;
  const { openModal } = useModal();
  const { openFullscreen } = useFullscreenModal();
  const displayingOldVersion = bidPackageVersion.id !== bidPackage.latestVersion.id;
  const [onlyChanged, setOnlyChanged] = useState(false);

  const tradePartnerIdsWithRevisions = bidPackage.latestVersion.latestBidContractRevisions
    .map((bcr) => bcr.bidContract.tradePartner?.id)
    .compact();
  const requestsWithoutContracts = bidPackage.activeRequests.filter(
    (r) => !tradePartnerIdsWithRevisions.includes(r.tradePartner.id),
  );
  const isDraft = bidPackage.status.code === BidPackageStatus.Draft;
  const scopeLineItems = useMemo(() => scopeLines.map((sl) => sl.takeoffLineItemVersion), [scopeLines]);

  function onCreateBid() {
    if (bidPackage.isUnitBased) {
      openModal({
        content: (
          <ImportBidModal
            requests={requestsWithEditableBids}
            bidPackageName={bidPackage.name}
            latestBidContractRevisions={bidPackage.latestVersion.latestBidContractRevisions}
            bidPackageVersionId={bidPackage.latestVersion.id}
          />
        ),
      });
    } else {
      openModal({
        content: (
          <SelectProrationModal
            requests={requestsWithoutContracts}
            bidPackageName={bidPackage.name}
            onContinue={(tradePartnerId, type) => {
              if (bidPackage.isInternalEstimate) {
                return openFullscreen(
                  <AddProrationBidModal
                    newBidContractParams={{
                      internalEstimateBidPackageId: bidPackage.id,
                      isInternalEstimate: true,
                      type,
                    }}
                    newBidContractRevisionParams={
                      bidPackage.internalEstimateBidContract
                        ? {
                            bidContractId: bidPackage.internalEstimateBidContract.id,
                            bidPackageVersionId: bidPackage.latestVersion.id,
                          }
                        : undefined
                    }
                    readyPlan={readyPlan}
                    readyPlanOptionsById={readyPlanOptionsById}
                    scopeLineItems={scopeLineItems}
                    tradePartnerName="Homebound"
                  />,
                );
              }
              const request = requestsWithoutContracts.find((r) => r.tradePartner.id === tradePartnerId);
              if (!request) fail("Unable to find request");
              openFullscreen(
                <AddProrationBidModal
                  newBidContractParams={{ bidRequestId: request.id, tradePartnerId, type }}
                  newBidContractRevisionParams={
                    request.bidContract
                      ? { bidContractId: request.bidContract.id, bidPackageVersionId: bidPackage.latestVersion.id }
                      : undefined
                  }
                  readyPlan={readyPlan}
                  readyPlanOptionsById={readyPlanOptionsById}
                  scopeLineItems={scopeLineItems}
                  tradePartnerName={request.tradePartner.name}
                />,
              );
            }}
            isInternalEstimate={bidPackage.isInternalEstimate}
          />
        ),
      });
    }
  }

  const onEditProratedBid = useCallback(
    function onEditProratedBid(bidContractRevisionId: string) {
      openFullscreen(
        <EditProrationBidModal
          bidContractRevisionId={bidContractRevisionId}
          readyPlan={readyPlan}
          readyPlanOptionsById={readyPlanOptionsById}
          scopeLineItems={scopeLineItems}
        />,
      );
    },
    [openFullscreen, readyPlan, readyPlanOptionsById, scopeLineItems],
  );

  const onUploadProratedBid = useCallback(
    function onUploadProratedBid(tradePartnerId: string) {
      openModal({
        content: (
          <ImportBidModal
            requests={bidPackage.activeRequests.filter((r) => r.tradePartner.id === tradePartnerId)}
            bidPackageName={bidPackage.name}
            latestBidContractRevisions={bidPackage.latestVersion.latestBidContractRevisions}
            bidPackageVersionId={bidPackage.latestVersion.id}
            isUnitBased={bidPackage.isUnitBased}
            tradePartnerId={tradePartnerId}
          />
        ),
      });
    },
    [bidPackage, openModal],
  );

  const rows = useMemo(() => {
    const allRows = createRows(bidPackage, bidPackageVersion, isDraft, scopeLines, readyPlanOptionsById);
    if (!onlyChanged) return allRows;
    return filterToChangedRows(allRows);
  }, [bidPackage, bidPackageVersion, scopeLines, readyPlanOptionsById, isDraft, onlyChanged]);

  const columns = useMemo(() => {
    // Instead of showing all options with a location, we want to show options that share a global option in different locations or are grouped by feature
    const allOptions = scopeLines
      .flatMap((scopeLine) => getReadyPlanOptions(readyPlanOptionsById, scopeLine.takeoffLineItemVersion.options))
      .unique();
    const optionsWithMultipleGlobalOptions = getOptionsInMultipleLocations(allOptions).map((o) => o.id);
    return createColumns(
      bidPackage,
      onEditProratedBid,
      onUploadProratedBid,
      isDraft,
      displayingOldVersion,
      optionsWithMultipleGlobalOptions,
    );
  }, [
    bidPackage,
    isDraft,
    onEditProratedBid,
    onUploadProratedBid,
    displayingOldVersion,
    readyPlanOptionsById,
    scopeLines,
  ]);

  const optionTypesGrouped = scopeLines
    .flatMap((scopeLine) => getReadyPlanOptions(readyPlanOptionsById, scopeLine.takeoffLineItemVersion.options))
    .unique()
    .groupBy((o) => o.type.id);
  const optionTypesCount = Object.entries(optionTypesGrouped).map(([, options]) => ({
    optionType: options[0].type.name,
    count: options.length,
  }));

  return (
    <>
      <div css={Css.df.w100.aic.gap2.py3.p2.$}>
        <div css={Css.xl.$}>Bid Total</div>
        {<Chips values={optionTypesCount.map(({ count, optionType }) => `${count} ${pluralize(count, optionType)}`)} />}
        {isDraft && <Checkbox label="Only Changed" selected={onlyChanged} onChange={setOnlyChanged} />}
        {!isDraft && !displayingOldVersion && (
          <div css={Css.mla.$}>
            <Button
              variant="text"
              label={bidPackage.isUnitBased ? "Upload Bid" : "Add Bid"}
              disabled={
                (bidPackage.status.code === BidPackageStatus.Awarded &&
                  "Revise bid package costs to enter new pricing") ||
                (bidPackage.isInternalEstimate &&
                  bidPackage.isUnitBased &&
                  "Currently not supported for Internal Estimates") ||
                ((bidPackage.isInternalEstimate
                  ? bidPackage.latestVersion.latestBidContractRevisions.nonEmpty
                  : (bidPackage.isUnitBased ? requestsWithEditableBids : requestsWithoutContracts).length === 0) &&
                  "Bids cannot be uploaded")
              }
              onClick={onCreateBid}
            />
          </div>
        )}
      </div>
      <GridTable columns={columns} filter={searchFilter} rows={rows} />
    </>
  );
}

type HeaderRow = { kind: "header"; data: { costData: TradeCostData } };
type OptionRow = {
  kind: "option";
  data: { options: PlanBasedBidsTable_ReadyPlanOptionFragment[]; costData: TradeCostData };
};
type TLIVRow = {
  kind: "tliv";
  data: {
    item: PlanBasedBidsTable_TakeoffLineItemVersionFragment;
    changeType: Maybe<string>;
    previous: Maybe<PlanBasedBidsTable_PreviousTakeoffLineItemVersionFragment>;
    costData: TradeCostData;
  };
};

type Row = HeaderRow | OptionRow | TLIVRow;

function createRows(
  bidPackage: PageBidPackageDetailFragment,
  bidPackageVersion: PlanBasedBidsTable_BidPackageVersionFragment,
  isDraft: boolean,
  scopeLines: PlanBasedBidsTable_ScopeLinesFragment[],
  readyPlanOptionsById: ReadyPlanOptionsById,
): GridDataRow<Row>[] {
  const trades = getBidPackageTradesWithEstimate(bidPackage);
  const tradeIds = trades.map((r) => r.tradePartner.id);
  const itemRows = scopeLines.map(({ takeoffLineItemVersion, changeType, previous }) => ({
    kind: "tliv" as const,
    id: takeoffLineItemVersion.id,
    data: {
      item: takeoffLineItemVersion,
      changeType,
      previous,
      // For each materialized scope line, each trade could have bid at most once on it
      costData: trades.keyBy(
        (bpr) => bpr.tradePartner.id,
        ({ bidContractRevision, tradePartner }) => {
          // If we are in draft mode we want to pull costs from the previous version revisions
          if (isDraft) {
            const previous = bidPackageVersion.previous;
            const previousRevision = bidPackage.isInternalEstimate
              ? previous?.latestBidContractRevisions.first
              : previous?.latestBidContractRevisions.find(
                  (bcr) => bcr.bidContract.tradePartner?.id === tradePartner.id,
                );
            if (bidPackage.isUnitBased) {
              const bcli = previousRevision?.lineItems.find(
                (li) => li.bidItem?.id === takeoffLineItemVersion.bidItem?.id,
              );
              // this is the unit cost on the BCLI
              return isNumber(bcli?.totalCostInCents)
                ? bcli?.totalCostInCents * getQuantity(takeoffLineItemVersion)
                : undefined;
            } else {
              /*
                Since we are looking at the previous version to show costs we need to used the identity since there may be a new TLIV
                additionally we want to make sure the options haven't changed between the versions
              */
              const revisionBcli = previousRevision?.lineItems.find(
                (li) =>
                  (li.itemTemplateItem as TakeoffLineItemVersion)?.identity.id === takeoffLineItemVersion.identity.id &&
                  takeoffLineItemVersion.options
                    .map((o) => o.id)
                    .hasSameElements(li.itemTemplateItem?.options.map((o) => o.id) ?? []),
              );
              // These are plan based tli therefore the total cost
              return revisionBcli?.totalCostInCents;
            }
          }
          // if bpr.bidcontract.type is unit based then use the bid contract line item total cost
          if (bidContractRevision?.bidContract?.type?.code === BidContractType.UnitBased) {
            const bcli = bidContractRevision.lineItems.find(
              (li) => li.bidItem?.id === takeoffLineItemVersion.bidItem?.id,
            );
            // this is the unit cost on the BCLI
            return isNumber(bcli?.totalCostInCents)
              ? bcli?.totalCostInCents * getQuantity(takeoffLineItemVersion)
              : undefined;
          }
          // for prorated bids we would want to use the itemtemplateitem cost in cents
          const revisionBcli = bidContractRevision?.lineItems.find(
            (li) => li.itemTemplateItem?.id === takeoffLineItemVersion.id,
          );
          // These are plan based tli therefore the total cost
          return revisionBcli?.totalCostInCents;
        },
      ),
    },
  }));

  const optionRows = itemRows
    .groupByObject((i) => getOptionsKey(i.data.item.options) || "base")
    .map(([key, children]) => {
      const { item } = children[0].data;
      return {
        kind: "option" as const,
        id: key,
        data: {
          options: getReadyPlanOptions(readyPlanOptionsById, item.options),
          costData: children
            // If we are showing removed items, don't sum up their costs
            .filter((c) => c.data.changeType !== AggregateMemberVersionChangeType.REMOVED)
            .map((c) => c.data.costData)
            .reduce(sumTradeCostData.bind(null, tradeIds), {}),
        },
        children,
      };
    });

  const headerWithCost = {
    ...simpleHeader,
    data: { costData: optionRows.map((r) => r.data.costData).reduce(sumTradeCostData.bind(null, tradeIds), {}) },
  };

  return [headerWithCost, ...optionRows];
}

function createColumns(
  bidPackage: PageBidPackageDetailFragment,
  onEditProratedBid: (bidContractRevisionId: string) => void,
  onUploadProratedBid: (tradePartnerId: string) => void,
  isDraft: boolean,
  displayingOldVersion: boolean,
  optionsWithMultipleGlobalOptions: string[],
): GridColumn<Row>[] {
  const tradeColumns = getBidPackageTradesWithEstimate(bidPackage);
  return [
    collapseColumn<Row>({
      tliv: (data) => ({ content: emptyCell.content, css: isDraft ? applyStyling(data) : undefined }),
    }),
    column<Row>({
      header: "Code",
      option: emptyCell,
      tliv: (data) => ({
        content: <DropCodeCell scope={data.item} />,
        css: isDraft ? applyStyling(data, "drop") : undefined,
      }),
      w: "90px",
    }),
    column<Row>({
      header: "Name",
      option: ({ options }) => ({
        content:
          options
            .map((o) => (optionsWithMultipleGlobalOptions.includes(o.id) ? `${o.name} - ${o.location?.name}` : o.name))
            .join(", ") || "Base",
        css: Css.smSb.$,
      }),
      tliv: (data) => ({
        content: [data.item.name, data.item.bidItem?.parentMaterialVariant?.code].compact().join(" "),
        css: isDraft ? applyStyling(data, "materialVariant") : undefined,
      }),
    }),
    column<Row>({
      header: "Qty",
      option: emptyCell,
      tliv: (data) => ({
        content: data.item.unitOfMeasure.useQuantity ? data.item.quantity : "N/A",
        css: isDraft ? applyStyling(data, "quantity") : undefined,
      }),
      w: "53px",
    }),
    column<Row>({
      header: "UoM",
      option: emptyCell,
      tliv: (data) => ({
        content: data.item.unitOfMeasure.shortName,
        css: isDraft ? applyStyling(data, "unitOfMeasure") : undefined,
      }),
      w: "67px",
    }),
    column<Row>({
      header: "Location",
      option: emptyCell,
      tliv: (data) => ({
        content: data.item.location.displayLocationPath,
        css: isDraft ? applyStyling(data, "location") : undefined,
      }),
      w: "160px",
    }),
    ...tradeColumns.map(({ tradePartner, bidContractRevision }, idx) => {
      const isProratedBid = bidContractRevision?.bidContract?.type?.code === BidContractType.GroupedByPlanAndOption;

      return numericColumn<Row>({
        // Add left border separator between trade columns (skip the first trade column)
        ...(idx > 0 ? { border: "left" as const } : {}),
        header: ({ costData }) => (
          <div css={Css.w100.df.aic.$}>
            <div css={Css.mra.$}>
              {tradePartner.name}
              {costData[tradePartner.id] && (
                <>
                  {" "}
                  (<Price valueInCents={costData[tradePartner.id]} />)
                </>
              )}
            </div>
            <div css={Css.df.gap1.$}>
              {!isDraft && !displayingOldVersion && isProratedBid && !bidPackage.isInternalEstimate && (
                <Tooltip title="Upload your prorated bid" placement="top">
                  <IconButton
                    icon="upload"
                    onClick={() => onUploadProratedBid(tradePartner.id)}
                    color={Palette.Blue600}
                    disabled={
                      bidPackage.status.code === BidPackageStatus.Awarded
                        ? "Revise bid package costs to enter new pricing"
                        : false
                    }
                  />
                </Tooltip>
              )}
              {!isDraft && !displayingOldVersion && isProratedBid && (
                <Tooltip title="Edit your prorated bid" placement="top">
                  <IconButton
                    icon="pencil"
                    onClick={() => onEditProratedBid(bidContractRevision?.id)}
                    color={Palette.Blue600}
                    disabled={
                      bidPackage.status.code === BidPackageStatus.Awarded
                        ? "Revise bid package costs to enter new pricing"
                        : false
                    }
                  />
                </Tooltip>
              )}
            </div>
          </div>
        ),
        option: ({ costData }) =>
          isNumber(costData[tradePartner.id]) ? priceCell({ valueInCents: costData[tradePartner.id] }) : emptyCell,
        tliv: (data) => ({
          content: isNumber(data.costData[tradePartner.id]) ? (
            <Price valueInCents={data.costData[tradePartner.id]} />
          ) : (
            emptyCell.content
          ),
          css: isDraft ? applyStyling(data) : undefined,
        }),
      });
    }),
  ];
}

type Field = keyof Pick<
  PlanBasedBidsTable_TakeoffLineItemVersionFragment,
  "options" | "quantity" | "unitOfMeasure" | "materialVariant" | "drop" | "location"
>;

function fieldHasChanged(data: TLIVRow["data"], field?: Field): boolean {
  if (!data.previous || !field) return false;
  if (field === "quantity") {
    return data.previous.quantity !== data.item.quantity;
  } else if (field === "options") {
    return optionsHaveChanged(data);
  }
  const previous = data.previous[field]?.id;
  const current = data.item[field]?.id;
  return previous !== current;
}

function applyStyling(data: TLIVRow["data"], field?: Field) {
  // We want to highlight the whole row if either the row change type is added or if the options on the row have changed
  if (data.changeType === AggregateMemberVersionChangeType.ADDED || fieldHasChanged(data, "options")) {
    return Css.bgYellow50.$;
  }
  if (data.changeType === AggregateMemberVersionChangeType.REMOVED) {
    return Css.bgGray50.tdlt.gray500.$;
  }
  if (data.changeType === AggregateMemberVersionChangeType.UPDATED && fieldHasChanged(data, field)) {
    return Css.bgYellow50.$;
  }
}

const allFields: Field[] = ["drop", "quantity", "unitOfMeasure", "materialVariant", "options", "location"];

function rowHasChanges(data: TLIVRow["data"]): boolean {
  if (data.changeType === AggregateMemberVersionChangeType.ADDED) return true;
  if (data.changeType === AggregateMemberVersionChangeType.REMOVED) return true;
  if (fieldHasChanged(data, "options")) return true;
  if (data.changeType === AggregateMemberVersionChangeType.UPDATED) {
    return allFields.some((f) => fieldHasChanged(data, f));
  }
  return false;
}

function filterToChangedRows(rows: GridDataRow<Row>[]): GridDataRow<Row>[] {
  return rows.flatMap((row) => {
    if (row.kind === "header") {
      return [row] as GridDataRow<Row>[];
    } else if (row.kind === "option") {
      // Look down into the tlivs for changes, and only return plan/parent rows with matching children
      const filteredTlivs = (row.children ?? []).filter(
        (tlivRow) => tlivRow.kind === "tliv" && rowHasChanges(tlivRow.data),
      );
      if (filteredTlivs.length === 0) return [];
      return [{ ...row, children: filteredTlivs }];
    } else {
      return [row] as GridDataRow<Row>[];
    }
  });
}

function optionsHaveChanged(data: TLIVRow["data"]) {
  const prev = data.previous;
  if (!prev || data.changeType !== AggregateMemberVersionChangeType.UPDATED) return false;
  return xor(
    prev.options?.map((o) => o.id),
    data.item.options?.map((o) => o.id),
  ).nonEmpty;
}

export function getQuantity(tliv: PlanBasedBidsTable_TakeoffLineItemVersionFragment) {
  return (tliv.unitOfMeasure.useQuantity ? tliv.quantity : 1) || 1;
}
